'use client';
import Image from 'next/image';
import { getOptimizedImageUrl } from '@bgzt/media';
import { Divider, IcBookmark, IcBookmarkFill, IcShare, Typography, theme } from '@bgzt/ui';
import clsx from 'clsx';

import { CONTENT_TYPE, EVENT_ACTION, EVENT_TYPE, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { AppDownloadTrigger } from '~/components';
import { useEnvironmentInfo, useFavorite, useSendLog, useShareDocument } from '~/hooks';

import { Banner, Benefit, Benefits, Images, ReviewSummary, Summary } from '~/app/products/[pid]/_components/Overview';
import { ProductDetail } from '~/app/products/[pid]/_queries';
import { generateImageUrl } from '~/app/products/[pid]/_utils';
import { OFFICIAL_BADGE_URL, PRODUCT_DETAIL_SNACKBAR_BOTTOM_OFFSET } from '~/app/products/[pid]/_components/constants';

import { utilStyles } from '~/styles';
import styles from './Overview.module.scss';
import { SamsungCardBenefit } from './SamsungCardBenefit';
import { TextModel } from '~/types/common';

type Props = {
  data: ProductDetail;
};

export const Overview = ({ data }: Props) => {
  const { isWebview } = useEnvironmentInfo();
  const sendLog = useSendLog();

  const share = useShareDocument();

  const shareProduct = () =>
    share({
      title: globalThis.window.document.title,
      log: {
        event_type: EVENT_TYPE.SELECT,
        page_id: PAGE_ID.PRODUCT,
        page_label: `${data.product.pid}`,
      },
    });

  const { isFavorite, toggle } = useFavorite(data.product.favorite);

  const toggleFavorite = (options: { from: 'TOP' | 'BOTTOM' }) => () => {
    if (!isWebview) return;

    toggle(data.product.pid, generateImageUrl(data.product.image.url, 0), {
      snackbarBottomOffset: PRODUCT_DETAIL_SNACKBAR_BOTTOM_OFFSET,
    });

    sendLog({
      event_type: EVENT_TYPE.WISHLIST,
      event_action: isFavorite ? EVENT_ACTION.REMOVE_FROM_WISHLIST : EVENT_ACTION.ADD_TO_WISHLIST,
      page_id: PAGE_ID.PRODUCT,
      view_id: VIEW_ID[options.from],
      content_type: CONTENT_TYPE.MERCARI,
    });
  };

  const payBenefitContents = [...(data?.payBenefit?.contents || [])];

  return (
    <div className={styles.wrapper}>
      {/* 상품 이미지 */}
      <Images
        data={Array.from({ length: data.product.image.count }, (_, index) =>
          generateImageUrl(getOptimizedImageUrl(data.product.image.url, 640, { resolutionMultiplier: 1 }), index),
        )}
        status={data.product.saleStatus}
      />

      <Banner price={data.product?.price} />

      {/* 상품 요약 */}
      <Summary data={data} />
      <Divider />

      {/* 상품 혜택 */}
      <Benefits>
        <Benefit
          title={'메루카리(mercari)'}
          iconUrl="mercari-static"
          titleBadge={
            <span className={styles.officialBadge}>
              <Image src={OFFICIAL_BADGE_URL} alt="official badge" width={21} height={12} unoptimized />
            </span>
          }
          contents={[
            '일본 현지 세컨핸드를 국내 상품처럼 간편하게 구매',
            ...(data?.shop?.uid ? [<ReviewSummary shopUid={data.shop.uid} key={data.shop.uid} />] : []),
          ]}
        />

        <Benefit
          title={data?.payBenefit?.title || ''}
          iconUrl={data?.payBenefit?.iconUrl || ''}
          contents={payBenefitContents}
          moreContents={data?.payBenefit?.moreContents}
        />
      </Benefits>

      {data?.bgztSamsungcardBenefit && (
        <SamsungCardBenefit
          title={data?.bgztSamsungcardBenefit?.title || ''}
          imageUrl={data?.bgztSamsungcardBenefit?.iconUrl || ''}
          appUrl={data?.bgztSamsungcardBenefit?.appUrl || ''}
          webUrl={data?.bgztSamsungcardBenefit?.webUrl || ''}
          contents={(data?.bgztSamsungcardBenefit?.contents as TextModel[]) || []}
        />
      )}

      {/* 찜하기 버튼 */}
      <AppDownloadTrigger>
        <button
          type="button"
          onClick={toggleFavorite({ from: 'TOP' })}
          className={clsx(styles.actionButton, styles.favoriteButton)}
        >
          <span className={utilStyles.visuallyHidden}>찜하기</span>
          {isFavorite ? (
            <IcBookmarkFill fill={theme.colors['red-500']} />
          ) : (
            <IcBookmark fill={theme.colors['gray-700']} />
          )}
        </button>
      </AppDownloadTrigger>

      {/* 공유 버튼 */}
      <div className={styles.subButtons}>
        <button onClick={shareProduct}>
          <IcShare size={18} fill={theme.colors['gray-600']} />
          <Typography variant="C2" color={theme.colors['gray-600']}>
            공유
          </Typography>
        </button>
        <AppDownloadTrigger>
          <button onClick={toggleFavorite({ from: 'BOTTOM' })}>
            {isFavorite ? (
              <IcBookmarkFill size={18} fill={theme.colors['red-500']} />
            ) : (
              <IcBookmark size={18} fill={theme.colors['gray-600']} />
            )}
            <Typography variant="C2" color={theme.colors['gray-600']}>
              찜
            </Typography>
          </button>
        </AppDownloadTrigger>
      </div>
    </div>
  );
};
